import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { BreakdownSense, StatusTone } from '@entities/Generation';
import { splitExample } from '@entities/Word';
import { markLabels, posName } from '@features/CardsWorkspace';
import { cn } from '@shared/lib';
import { StatusGlyph } from '@shared/ui';

export const TONE_CLASS: Record<StatusTone, string> = {
  accent: 'text-accent-hi',
  warn: 'text-warn',
  muted: 'text-muted',
  plain: '',
};

/** Кружок загрузки кнопок записи; при «меньше движения» — статичная дуга. */
export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cn('h-4 w-4 animate-spin motion-reduce:animate-none', className)} viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.2" />
      <path d="M16 9a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/** Полоса скелетона (62 % в колонке «пример», 84 % — в «смысле»). */
export function Bar({ width }: { width: string }) {
  return <span className="gl-bar animate-skeleton motion-reduce:animate-none" style={{ width }} aria-hidden />;
}

export interface SenseLineProps {
  sense: BreakdownSense;
  /** Слово карточки (у фразы — сама фраза) — выделяется в примере. */
  word: string;
  /** Подписывать часть речи сокращением — у значений слова; у фраз её не подписываем. */
  showPos: boolean;
  /** Первая строка фразы или слова подборки: сама фраза с пометами над переводом. */
  head?: string | null;
  /** Статус в колонке «пример» первой строки идущего элемента: «генерируется», «дописываем часть речи». */
  status?: { label: string; tone: StatusTone; ring: boolean } | null;
  /** Действие справа: «Ввести снова» в первой недобравшейся строке. */
  action?: ReactNode;
}

/**
 * Строка значения в раскрытой записи «Недавно добавленных» (ТЗ главной § 22.5): «перевод | пример с
 * переводом | смысл», без рамок, без подсказки и без корзины — только просмотр. Стопкой (узкое тело) —
 * те же части сверху вниз; высота строки — по тексту. Чего нет в данных — не рисуем.
 */
export function SenseLine({ sense, word, showPos, head = null, status = null, action }: SenseLineProps) {
  const { t } = useTranslation();
  const marks = markLabels(t, sense.marks);
  const pending = sense.status === 'generating';
  const failed = sense.status === 'failed';
  const removed = sense.status === 'removed';
  const ready = sense.status === 'ready';
  const posShort = showPos && sense.pos ? posName(t, sense.pos, true) : null;
  const posFull = showPos && sense.pos ? posName(t, sense.pos) : null;
  const example = ready && sense.example ? splitExample(sense.example.en, word) : null;

  return (
    <div className={cn('gl-r gl-line', removed && 'opacity-60')}>
      <div className="gl-c1 min-w-0">
        {head && (
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span lang="en" className="gl-phrase">
              {head}
            </span>
            {marks.length > 0 && <span className="gl-marks !ml-0">{marks.join(', ')}</span>}
          </div>
        )}
        <div className="gl-tr">
          {posShort && posFull && (
            <>
              {/* Полное название — для экранного диктора, сокращение — глазами (§ 22.11). */}
              <abbr title={posFull} aria-hidden className="gl-pos">
                {posShort}
              </abbr>
              <span className="sr-only">{posFull} </span>
            </>
          )}
          <span>{sense.translation}</span>
          {!head && marks.length > 0 && <span className="gl-marks">{marks.join(', ')}</span>}
        </div>
      </div>
      <div className="gl-c2 min-w-0">
        {status ? (
          <span className={cn('inline-flex items-center gap-1.5', TONE_CLASS[status.tone])}>
            {status.ring && <StatusGlyph kind="generating" size={12} />}
            {status.label}
          </span>
        ) : pending ? (
          <Bar width="62%" />
        ) : failed ? (
          <span className="text-warn">{t('generations.item.notAdded')}</span>
        ) : removed ? (
          <span className="text-muted">{t('generations.item.removed')}</span>
        ) : example ? (
          <>
            <div lang="en" className="gl-ex">
              {example.pre}
              {example.word && <b>{example.word}</b>}
              {example.post}
            </div>
            {sense.example?.ru && <div className="gl-ex-ru">{sense.example.ru}</div>}
          </>
        ) : null}
      </div>
      <div className="gl-c3 min-w-0">
        {action ? (
          <div className="gl-act-cell">{action}</div>
        ) : pending ? (
          <Bar width="84%" />
        ) : ready && sense.definition ? (
          // Подпись обязательна: без неё смысл читался как перевод примера (правило опыта).
          <div className="gl-def">
            <span className="gl-def-label">{t('sessionSenses.definitionLabel')}</span>
            {sense.definition}
          </div>
        ) : null}
      </div>
    </div>
  );
}
